import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PackagePlus, RotateCcw } from "lucide-react";
import api, { errMsg } from "@/lib/api";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** After an import rejected rows for unknown FG codes: add them to Customer Master (with UPH) and re-run only the rejected rows. */
export function MissingFgFix({ result, name, retryUrl, addUrl, onResult, onProgress }) {
  const qc = useQueryClient();
  const [items, setItems] = useState(() => (result.missing_fgs || []).map((m) => ({ ...m, model: "", mva: "", uph: "" })));
  const [busy, setBusy] = useState(false);
  if (!items.length || !result.job_id) return null;
  const set = (i, k, v) => setItems(items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));

  const retry = async (jobId) => {
    let { data } = await api.post(`${retryUrl}/${jobId}`);
    onProgress?.({ phase: "rows", done: 0, total: data.rows, started: Date.now() });
    for (;;) {
      await sleep(800);
      const { data: j } = await api.get(`/jobs/${data.job_id}`);
      onProgress?.({ phase: "rows", done: j.done, total: j.total || data.rows, started: Date.now() });
      if (j.status === "error") throw new Error(j.error || "Retry failed");
      if (j.status === "done") return { ...(j.result || {}), job_id: data.job_id, retried_from: jobId };
    }
  };

  const run = async (withAdd) => {
    setBusy(true);
    try {
      if (withAdd) {
        const { data } = await api.post(addUrl, { items: items.map((m) => ({ fg_code: m.fg_code, model: m.model || m.fg_code, customer: m.customer, mva_per_unit: m.mva === "" ? null : Number(m.mva), uph: m.uph === "" ? null : Number(m.uph) })) });
        toast.success(`Added ${data.created.length} FG code(s) to Customer Master${data.skipped.length ? ` · ${data.skipped.length} already existed` : ""}`);
        if (data.errors?.length) toast.error(data.errors.map((e) => `${e.key}: ${e.error}`).join("; "));
        qc.invalidateQueries({ queryKey: ["masters"] });
      }
      const r = await retry(result.job_id);
      toast[r.errors.length ? "warning" : "success"](`Re-ran ${r.rows} rejected row(s): ${r.created} created, ${r.updated} updated${r.errors.length ? `, ${r.errors.length} still failing` : ""}`);
      onResult(r);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); onProgress?.(null); }
  };

  const fixedRows = items.reduce((s, m) => s + m.rows, 0);
  return (
    <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs font-sans" data-testid={`missing-fg-fix-${name}`}>
      <div className="font-semibold text-amber-900">{items.length} FG code(s) are not in Customer Master — {fixedRows} row(s) were skipped because of this.</div>
      <div className="text-amber-800 mb-2">Fill the details (MVA comes from Costing → Landed Cost Book by EMS SO + FG) and add them in one click; the skipped rows are then re-uploaded automatically.</div>
      <table className="w-full text-xs"><thead><tr className="pp-label text-left"><th className="py-1 pr-2">FG Code</th><th className="py-1 pr-2">Rows</th><th className="py-1 pr-2">Customer *</th><th className="py-1 pr-2">Model / Description</th><th className="py-1 pr-2">UPH</th></tr></thead>
        <tbody>{items.map((m, i) => <tr key={m.fg_code} className="border-t border-amber-200" data-testid={`missing-fg-row-${m.fg_code}`}>
          <td className="py-1 pr-2 font-mono font-semibold">{m.fg_code}</td><td className="py-1 pr-2 font-mono">{m.rows}</td>
          <td className="py-1 pr-2"><input className="pp-input h-7 w-36 text-xs" value={m.customer} onChange={(e) => set(i, "customer", e.target.value)} placeholder="Customer" data-testid={`missing-fg-customer-${m.fg_code}`} /></td>
          <td className="py-1 pr-2"><input className="pp-input h-7 w-44 text-xs" value={m.model} onChange={(e) => set(i, "model", e.target.value)} placeholder={m.fg_code} data-testid={`missing-fg-model-${m.fg_code}`} /></td>
          
          <td className="py-1 pr-2"><input type="number" min="0" className="pp-input h-7 w-20 text-xs font-mono" value={m.uph} onChange={(e) => set(i, "uph", e.target.value)} placeholder="0" data-testid={`missing-fg-uph-${m.fg_code}`} /></td>
        </tr>)}</tbody></table>
      <div className="mt-2 flex flex-wrap gap-2">
        <button type="button" className="pp-btn-primary h-8 px-3 text-xs" disabled={busy || items.some((m) => !m.customer.trim())} onClick={() => run(true)} data-testid={`missing-fg-add-retry-${name}`}><PackagePlus size={13} /> {busy ? "Working…" : `Add ${items.length} FG code(s) & re-upload ${fixedRows} row(s)`}</button>
        <button type="button" className="pp-btn-ghost h-8 px-3 text-xs" disabled={busy} onClick={() => run(false)} data-testid={`missing-fg-retry-${name}`} title="Use after fixing Customer Master manually"><RotateCcw size={13} /> Re-upload rejected rows only</button>
      </div>
    </div>
  );
}
